<template>
  <div class="flex flex-col gap-2">
    <BaseToggle
      v-if="isMounted"
      label="Nur Adaptererfassung"
      test-id="adapterOnlyToggle"
      :is-checked="isAdapterOnly"
      @is-toggled="toggleAdapterOnly"
    />

    <AdapterForm ref="adapterFormRef" :inspection-point="inspectionPoint" />

    <SRTInspectionForm
      v-if="!isAdapterOnly && device"
      ref="srtFormRef"
      :device="device"
      :installation-point="inspectionPoint"
    />

    <BaseTextarea
      v-model:input-text="inspectionPoint.note"
      label="Notiz"
      :max-length="NOTE_MAX_LENGTH"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, inject, ref, Ref, watch } from 'vue';
import BaseToggle from '@/components/UI/Switch/BaseToggle.vue';
import AdapterForm from '@/components/Forms/DeviceFormComponents/DeviceTypeForms/InspectionForms/AdapterForm.vue';
import SRTInspectionForm from '@/components/Forms/DeviceFormComponents/DeviceTypeForms/InspectionForms/SRTInspectionForm.vue';
import {
  InspectionPoint,
  InspectionPointType,
} from '@/models/inspectionPoint/InspectionPoint';
import { SmartRadiatorThermostat } from '@/models/inspectionPoint/InspectionDevice';
import BaseTextarea from '@/components/UI/Note/BaseTextarea.vue';
import { NOTE_MAX_LENGTH } from '@/enums/generic';

const emit = defineEmits<{ (e: 'enable-srt-device'): void }>();
const props = defineProps<{ inspectionPoint: InspectionPoint }>();
const deviceModel = defineModel<SmartRadiatorThermostat | undefined>('device');
const device = computed(() => deviceModel.value);

const inspectionPoint = computed(() => props.inspectionPoint);
const isMounted = inject('isMounted') as Ref<boolean>;
const adapterFormRef = ref<{
  isFormValid?: boolean | Ref<boolean>;
  saveImage?: (id: string) => void;
} | null>(null);
const srtFormRef = ref<{
  isFormValid?: boolean | Ref<boolean>;
  saveImage?: (id: string) => void;
} | null>(null);

const adapterOnly = ref(
  inspectionPoint.value.inspectionData.inspectionType ===
    InspectionPointType.SRT_ADAPTER || !inspectionPoint.value.activeTestDeviceId
);
const isAdapterOnly = computed(() => adapterOnly.value);
const removedDevice = ref<SmartRadiatorThermostat | undefined>();
const pendingDeletionDevice = ref<SmartRadiatorThermostat | undefined>();

watch(
  () => adapterOnly.value,
  (value) => {
    inspectionPoint.value.inspectionData.inspectionType = value
      ? InspectionPointType.SRT_ADAPTER
      : undefined;
  },
  { immediate: true }
);

const toggleAdapterOnly = (isToggled: boolean) => {
  if (!isMounted.value) return;
  if (adapterOnly.value === isToggled) {
    return;
  }

  adapterOnly.value = isToggled;

  if (isToggled) {
    if (deviceModel.value) {
      removedDevice.value = deviceModel.value;
      pendingDeletionDevice.value = deviceModel.value;
    }
    inspectionPoint.value.unsetActiveDevice();
    deviceModel.value = undefined;
    return;
  }

  if (removedDevice.value) {
    deviceModel.value = removedDevice.value;
    inspectionPoint.value.activeTestDeviceId = removedDevice.value.id;
    pendingDeletionDevice.value = undefined;
    return;
  }

  emit('enable-srt-device');
};

const resolveValidity = (value?: boolean | Ref<boolean>) => {
  if (typeof value === 'boolean') {
    return value;
  }
  return value?.value ?? false;
};

const adapterFormIsValid = computed(() => {
  return resolveValidity(adapterFormRef.value?.isFormValid);
});

const srtFormIsValid = computed(() => {
  if (isAdapterOnly.value) {
    return true;
  }
  if (!deviceModel.value) {
    return false;
  }
  return resolveValidity(srtFormRef.value?.isFormValid);
});

defineExpose({
  isFormValid: computed(() => adapterFormIsValid.value && srtFormIsValid.value),
  saveImage: (objectId: string) => {
    adapterFormRef.value?.saveImage?.(objectId);
    if (!isAdapterOnly.value) {
      srtFormRef.value?.saveImage?.(objectId);
    }
  },
  getDevicePendingDeletion: () => pendingDeletionDevice.value,
  clearPendingDeletion: () => {
    pendingDeletionDevice.value = undefined;
  },
});
</script>
